window.RosentalDiscount = (() => {
  try {
    sessionStorage.removeItem('discountCodeOnly');
    localStorage.removeItem('discountCodeOnly');
  } catch (e) {
  }
  const CART_CONFIG_KEY = 'rosental_cart_config';
  const CART_CACHE_KEY = 'rosental_cart_cache';
  const CACHE_DURATION = 720000;
  const cartEventListeners = new Map();
  let cartUpdateQueue = [];
  let cartUpdateTimeout = null;
  const CART_DEBOUNCE_DELAY = 200;
  let lastCartEventTime = 0;
  const MIN_CART_EVENT_INTERVAL = 100;
  function createCartConfiguration() {
    return {
      activeCode: null,
      activeDiscount: null,
      cart: {
        items: [],
        total_price: 0,
        item_count: 0,
        cart_level_discount_applications: [],
        discount_codes: []
      },
      timestamp: Date.now(),
      lastEventId: 0
    };
  }
  function getCartConfiguration() {
    try {
      const cached = localStorage.getItem(CART_CONFIG_KEY);
      if (!cached) {
        if (!window.__lastNoConfigLog) {
          window.__lastNoConfigLog = Date.now();
        }
        return null;
      }
      const data = JSON.parse(cached);
      const now = Date.now();
      if (now - data.timestamp > CACHE_DURATION) {
        if (!window.__lastCacheExpiredLog) {
          window.__lastCacheExpiredLog = Date.now();
        }
        localStorage.removeItem(CART_CONFIG_KEY);
        return null;
      }
      if (window.__lastConfigLog !== JSON.stringify(data)) {
        window.__lastConfigLog = JSON.stringify(data);
      }
      return data;
    } catch (e) {
      return null;
    }
  }
  function setCartConfiguration(data) {
    try {
      const config = {
        ...data,
        timestamp: Date.now()
      };
      localStorage.setItem(CART_CONFIG_KEY, JSON.stringify(config));
      if (window.__lastSetConfigLog !== JSON.stringify(config)) {
        window.__lastSetConfigLog = JSON.stringify(config);
      }
      if (data.activeCode && (!window.__lastLoggedCode || window.__lastLoggedCode !== data.activeCode)) {
        window.__lastLoggedCode = data.activeCode;
      }
    } catch (e) {
    }
  }
  function clearCartConfiguration() {
    try {
      localStorage.removeItem(CART_CONFIG_KEY);
    } catch (e) {
    }
  }
  let isFetchingCart = false;
  async function fetchCartData(force = false) {
    if (isFetchingCart) {
      if (!window.__lastFetchInProgressLog) {
        window.__lastFetchInProgressLog = Date.now();
      }
      const config = getCartConfiguration();
      return config?.cart || { items: [], total_price: 0, cart_level_discount_applications: [], discount_codes: [] };
    }
    isFetchingCart = true;
    try {
      if (!force) {
        const config = getCartConfiguration();
        if (config?.cart) {
          isFetchingCart = false;
          return config.cart;
        }
      }
      if (!window.__lastFetchFreshLog) {
        window.__lastFetchFreshLog = Date.now();
      }
      const response = await fetch('/cart.js', { _isCartConfigRequest: true });
      if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
      const cart = await response.json();
      const config = getCartConfiguration() || createCartConfiguration();
      config.cart = cart;
      setCartConfiguration(config);
      if (!window.__lastCartFetchedLog) {
        window.__lastCartFetchedLog = Date.now();
      }
      if (!window.__isDispatchingConfigEvent) {
        setTimeout(() => {
          syncCartConfiguration();
        }, 50);
      }
      return cart;
    } catch (error) {
      if (!window.__lastCartFetchFailedLog) {
        window.__lastCartFetchFailedLog = Date.now();
      }
      const config = getCartConfiguration() || createCartConfiguration();
      const fallbackCart = { items: [], total_price: 0, cart_level_discount_applications: [], discount_codes: [] };
      config.cart = fallbackCart;
      setCartConfiguration(config);
      return fallbackCart;
    } finally {
      isFetchingCart = false;
    }
  }
  function isKnownDiscountCode(code) {
    if (!code) return false;
    const normalized = String(code).trim().toUpperCase();
    const discounts = window.themeDiscounts || [];
    if (!Array.isArray(discounts)) return false;
    return discounts.some(d => {
      if (Array.isArray(d?.codes)) {
        return d.codes.some(c => String(c || '').trim().toUpperCase() === normalized);
      }
      const dc = String(d?.code || '').trim().toUpperCase();
      return dc && dc === normalized;
    });
  }

  function updateActiveDiscount() {
    const code = window.CookieHandler?.getActiveCode?.();
    const discounts = window.themeDiscounts || [];
    if (!code || !Array.isArray(discounts)) {
      return null;
    }
    const normalizedCookie = String(code || '').trim().toUpperCase();
    
    // Suche nach Discount mit codes Array (neue Struktur)
    let activeDiscount = discounts.find(d => {
      if (Array.isArray(d?.codes)) {
        return d.codes.some(c => {
          const dc = String(c || '').trim().toUpperCase();
          return dc === normalizedCookie;
        });
      }
      return false;
    });
    
    // Fallback: Suche nach Discount mit einzelner code Property (alte Struktur)
    if (!activeDiscount) {
      activeDiscount = discounts.find(d => {
        const dc = String(d?.code || '').trim().toUpperCase();
        if (!dc || !normalizedCookie) return false;
        return dc === normalizedCookie;
      });
    }
    
    if (!activeDiscount) {
      return null;
    }
    
    // Bestimme den gefundenen Code (für codes Array den tatsächlich gefundenen Code verwenden)
    let foundCode = normalizedCookie;
    if (Array.isArray(activeDiscount.codes)) {
      const matchedCode = activeDiscount.codes.find(c => {
        const dc = String(c || '').trim().toUpperCase();
        return dc === normalizedCookie;
      });
      if (matchedCode) {
        foundCode = String(matchedCode).trim().toUpperCase();
      }
    } else if (activeDiscount.code) {
      foundCode = String(activeDiscount.code).trim().toUpperCase();
    }
    
    const relevantDiscount = {
      code: foundCode,
      codes: activeDiscount.codes || undefined, // Behalte codes Array für zukünftige Verwendung
      type: activeDiscount.type,
      value: activeDiscount.value,
      startdate: activeDiscount.startdate,
      enddate: activeDiscount.enddate,
      show_5_extra: activeDiscount.show_5_extra || false,
      tiers: activeDiscount.tiers?.map(tier => ({
        value: tier.value,
        type: tier.type,
        applies_to_products: tier.applies_to_products,
        applies_to_collection: tier.applies_to_collection,
        applies_to_price: tier.applies_to_price
      })),
      free_gift: activeDiscount.free_gift ? {
        threshold: activeDiscount.free_gift.threshold,
        products: activeDiscount.free_gift.products,
        product_handles: activeDiscount.free_gift.product_handles || []
      } : null
    };
    return relevantDiscount;
  }
  function syncCartConfiguration() {
    if (window.__isDispatchingConfigEvent || isFetchingCart) {
      if (!window.__lastSyncSkippedLog) {
        window.__lastSyncSkippedLog = Date.now();
      }
      return;
    }
    window.__isDispatchingConfigEvent = true;
    const activeDiscount = updateActiveDiscount();
    const config = getCartConfiguration() || createCartConfiguration();
    const oldActiveCode = config.activeCode;
    const oldCartItemCount = config.cart?.item_count || 0;
    config.activeCode = window.CookieHandler?.getActiveCode?.();
    config.activeDiscount = activeDiscount;
    config.lastEventId++;
    setCartConfiguration(config);
    const eventDetail = {
      config: config,
      eventId: config.lastEventId,
      changes: {
        activeCodeChanged: oldActiveCode !== config.activeCode,
        cartItemsChanged: oldCartItemCount !== (config.cart?.item_count || 0),
        hasActiveDiscount: !!config.activeDiscount
      }
    };
    if (!window.__cartConfigUpdateQueued) {
      window.__cartConfigUpdateQueued = true;
      queueMicrotask(() => {
        window.__cartConfigUpdateQueued = false;
        window.dispatchEvent(new CustomEvent('cart-config:update', { detail: eventDetail }));
      });
    }
    setTimeout(() => {
      window.__isDispatchingConfigEvent = false;
    }, 100);
  }
  function getActiveCode() {
    return window.CookieHandler?.getActiveCode?.();
  }
  function getActiveDiscount() {
    const config = getCartConfiguration();
    const discount = config?.activeDiscount || updateActiveDiscount();
    const discountKey = `${discount?.code || 'NONE'}-${discount?.value || 0}-${discount?.tiers?.length || 0}`;
    if (window.__lastActiveDiscountLog !== discountKey) {
      window.__lastActiveDiscountLog = discountKey;
    }
    return discount;
  }
  function findDiscountTier(productId, collectionIds = []) {
    const discount = getActiveDiscount();
    if (!discount?.tiers) {
      if (!window.__lastNoTiersLog) {
        window.__lastNoTiersLog = Date.now();
      }
      return null;
    }
    const tiers = discount.tiers;
    const pid = String(productId);
    const collectionSet = new Set((collectionIds || []).map(String));
    const foundTier = tiers.find(tier => {
      const products = (tier.applies_to_products || []).map(String);
      const collections = (tier.applies_to_collection || []).map(String);
      return products.includes(pid) || collections.some(cid => collectionSet.has(cid));
    });
    const tierKey = `${productId}-${discount?.code || 'NONE'}-${foundTier?.value || 0}`;
    if (window.__lastFindTierLog !== tierKey) {
      window.__lastFindTierLog = tierKey;
    }
    return foundTier;
  }
  function getMaxTier(discount) {
    if (!discount?.tiers?.length) return null;
    return discount.tiers.reduce((best, tier) => tier.value > best.value ? tier : best, { value: 0 });
  }
  function formatMoney(amount) {
    try {
      return (amount / 100).toFixed(2).replace('.', ',') + ' €';
    } catch {
      return amount + ' €';
    }
  }
  function subscribeToCartEvents(event, callback) {
    if (!cartEventListeners.has(event)) {
      cartEventListeners.set(event, new Set());
    }
    cartEventListeners.get(event).add(callback);
    if (!window.__lastSubscribeLog || window.__lastSubscribeLog !== event) {
      window.__lastSubscribeLog = event;
    }
  }
  function unsubscribeFromCartEvents(event, callback) {
    if (cartEventListeners.has(event)) {
      cartEventListeners.get(event).delete(callback);
      if (!window.__lastUnsubscribeLog || window.__lastUnsubscribeLog !== event) {
        window.__lastUnsubscribeLog = event;
      }
    }
  }
  function dispatchCartEvent(event, data) {
    const now = Date.now();
    if (now - lastCartEventTime < MIN_CART_EVENT_INTERVAL) {
      return;
    }
    lastCartEventTime = now;
    if (cartEventListeners.has(event)) {
      cartEventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
        }
      });
    }
    window.dispatchEvent(new CustomEvent(event, { 
      detail: data,
      bubbles: true 
    }));
    if (!window.__lastCartEventLog || window.__lastCartEventLog !== event) {
      window.__lastCartEventLog = event;
    }
  }
  function queueCartUpdate(update) {
    const now = Date.now();
    if (now - lastCartEventTime < MIN_CART_EVENT_INTERVAL) {
      return;
    }
    cartUpdateQueue.push(update);
    if (cartUpdateTimeout) {
      clearTimeout(cartUpdateTimeout);
    }
    cartUpdateTimeout = setTimeout(() => {
      processCartUpdateQueue();
    }, CART_DEBOUNCE_DELAY);
    if (!window.__lastQueueLog || window.__lastQueueLog !== cartUpdateQueue.length) {
      window.__lastQueueLog = cartUpdateQueue.length;
    }
  }
  async function processCartUpdateQueue() {
    if (cartUpdateQueue.length === 0) return;
    const updates = cartUpdateQueue.splice(0);
    if (!window.__lastProcessingLog || window.__lastProcessingLog !== updates.length) {
      window.__lastProcessingLog = updates.length;
    }
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (response.ok) {
        const result = await response.json();
        dispatchCartEvent('cart:change', result);
        if (!window.__lastSuccessLog) {
          window.__lastSuccessLog = Date.now();
        }
      } else {
        if (!window.__lastCartUpdatesFailedLog) {
          window.__lastCartUpdatesFailedLog = Date.now();
        }
        dispatchCartEvent('cart:error', { status: response.status });
      }
    } catch (error) {
      if (!window.__lastCartProcessingFailedLog) {
        window.__lastCartProcessingFailedLog = Date.now();
      }
      dispatchCartEvent('cart:error', { error: error.message });
    }
  }
  function bindCookieEvents(callback) {
    if (typeof callback !== 'function') return () => {};
    const boundCallback = () => {
      if (typeof callback === 'function') {
        callback();
      }
    };
    window.addEventListener('cookie:discount:change', boundCallback);
    return () => {
      window.removeEventListener('cookie:discount:change', boundCallback);
    };
  }
  function bindCartEvents(callback) {
    return bindCookieEvents(callback);
  }
  function getCartData() {
    const config = getCartConfiguration();
    return config?.cart || { items: [], total_price: 0, cart_level_discount_applications: [], discount_codes: [] };
  }
  function getActiveDiscountData() {
    const config = getCartConfiguration();
    return config?.activeDiscount || null;
  }
  function getActiveCodeData() {
    const config = getCartConfiguration();
    return config?.activeCode || null;
  }
  function isCartConfigurationReady() {
    const config = getCartConfiguration();
    return !!config && !!config.cart;
  }
  function onCartConfigUpdate(callback) {
    if (typeof callback !== 'function') return () => {};
    const handler = (event) => {
      const detail = event && event.detail ? event.detail : {};
      const config = detail.config || getCartConfiguration() || null;
      const eventId = detail.eventId || Date.now();
      const changes = detail.changes || {};
      if (window.__isDispatchingConfigEvent) return;
      callback({ config, eventId, changes });
    };
    window.addEventListener('cart-config:update', handler);
      return () => {
      window.removeEventListener('cart-config:update', handler);
    };
  }
  let cartRequestCount = 0;
  let lastCartRequestTime = 0;
  let shopifyRateLimited = false;
  let rateLimitBackoff = 0;
  const MAX_CART_REQUESTS_PER_SECOND = 5;
  const MIN_REQUEST_INTERVAL = 200;
  const MAX_CART_REQUESTS_PER_MINUTE = 120;
  function setupFetchInterceptor() {
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      const [url, options = {}] = args;
      if (url.includes('/cart.js') && options._isCartConfigRequest) {
        return originalFetch.apply(this, args);
      }
      if (url.includes('/cart/') || url.includes('/cart.js') || url.includes('cart/add') || url.includes('cart/update') || url.includes('cart/change') || url.includes('cart/prepare_shipping_rates') || url.includes('cart/async_shipping_rates')) {
        if (!window.__lastCartRequestLog || window.__lastCartRequestLog !== url) {
          window.__lastCartRequestLog = url;
        }
        const now = Date.now();
        if (shopifyRateLimited && now < rateLimitBackoff) {
          if (!window.__lastRateLimitLog) {
            window.__lastRateLimitLog = Date.now();
          }
          return originalFetch.apply(this, args);
        }
        if (shopifyRateLimited && now >= rateLimitBackoff) {
          if (!window.__lastBackoffEndedLog) {
            window.__lastBackoffEndedLog = Date.now();
          }
          shopifyRateLimited = false;
          rateLimitBackoff = 0;
        }
        if (now - lastCartRequestTime < MIN_REQUEST_INTERVAL) {
          if (!window.__lastThrottleLog || window.__lastThrottleLog !== url) {
            window.__lastThrottleLog = url;
          }
          return originalFetch.apply(this, args);
        }
        if (cartRequestCount >= MAX_CART_REQUESTS_PER_MINUTE) {
          if (!window.__lastRequestLimitLog) {
            window.__lastRequestLimitLog = Date.now();
          }
          return originalFetch.apply(this, args);
        }
        try {
          cartRequestCount++;
          lastCartRequestTime = now;
          const response = await originalFetch.apply(this, args);
          if (response.status === 429) {
            if (!window.__lastRateLimitDetectedLog) {
              window.__lastRateLimitDetectedLog = Date.now();
            }
            shopifyRateLimited = true;
            rateLimitBackoff = now + 30000;
            return response;
          }
          if (response.ok && (url.includes('/cart.js') || url.includes('/cart/'))) {
            if (!window.__lastImmediateUpdateLog) {
              window.__lastImmediateUpdateLog = Date.now();
            }
            const responseClone = response.clone();
            const cartData = await responseClone.json();
            updateCartConfigurationFromResponseImmediate(cartData, url);
          }
          return response;
        } catch (error) {
          if (!window.__lastFetchErrorLog) {
            window.__lastFetchErrorLog = Date.now();
          }
          return originalFetch.apply(this, args);
        }
      }
      return originalFetch.apply(this, args);
    };
    setInterval(() => {
      cartRequestCount = 0;
      if (!window.__lastResetLog) {
        window.__lastResetLog = Date.now();
      }
    }, 60000);
  }
  function initialize() {
    const existingConfig = getCartConfiguration();
    const activeCode = window.CookieHandler?.getActiveCode?.();
    const cookieRestored = sessionStorage.getItem('rosental_cookie_restored');
    if (!activeCode && !cookieRestored) {
      sessionStorage.setItem('rosental_cookie_restored', '1');
      fetch('/cart.js')
        .then(r => r.json())
        .then(cart => {
          const cartCode = (cart.discount_codes || []).find(dc => dc.applicable !== false)?.code;
          if (cartCode) {
            if (isKnownDiscountCode(cartCode)) {
              window.CookieHandler?.setCookie?.(cartCode, false);
            } else {
              sessionStorage.setItem('pending_discount_code', cartCode.toUpperCase());
            }
          }
        })
        .finally(() => {
          continueRosentalInit();
        });
      return;
    }
    continueRosentalInit();
  }
  function continueRosentalInit() {
    const existingConfig = getCartConfiguration();
    const activeCode = window.CookieHandler?.getActiveCode?.();
    if (activeCode && (!existingConfig || !existingConfig.cart || !existingConfig.activeCode)) {
      fetchCartData(true).then(cart => {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: activeCode })
        }).then(() => {
          syncCartConfiguration();
        });
      });
    } else if (!existingConfig) {
      fetchCartData(true).then(cart => {
        const initialConfig = createCartConfiguration();
        initialConfig.cart = cart;
        setCartConfiguration(initialConfig);
      }).catch(() => {
        const initialConfig = createCartConfiguration();
        setCartConfiguration(initialConfig);
      });
    }
    setTimeout(() => {
      syncCartConfiguration();
    }, 200);
    setupCartConfigEventShim();
    setupFetchInterceptor();
    setupManualTriggers();
  }
  function setupCartConfigEventShim() {
    if (window.__cartConfigShimInstalled) return;
    window.__cartConfigShimInstalled = true;
    const attachShim = (target) => {
      if (!target?.addEventListener) return;
      target.addEventListener('cart-config:update', (event) => {
        if (event?.detail?.__normalized) return;
        if (event?.detail == null) {
          try { event.stopImmediatePropagation(); } catch (e) {}
          const normalizedDetail = {
            config: getCartConfiguration(),
            eventId: Date.now(),
            changes: {},
            __normalized: true
          };
          try {
            target.dispatchEvent(new CustomEvent('cart-config:update', { detail: normalizedDetail }));
          } catch (e) {
            window.dispatchEvent(new CustomEvent('cart-config:update', { detail: normalizedDetail }));
          }
        }
      }, { capture: true });
    };
    attachShim(window);
    attachShim(document);
    attachShim(document.documentElement);
  }
  let isValidatingPending = false;
  async function validatePendingCode(cartData) {
    const pendingCode = sessionStorage.getItem('pending_discount_code');
    if (!pendingCode || isValidatingPending) return;
    if (sessionStorage.getItem('pending_code_validated')) return;
    if (!cartData || (cartData.item_count || 0) === 0) return;

    const currentCode = window.CookieHandler?.getActiveCode?.();
    const autoCode = (window.__autoApplyCode || '').toUpperCase();
    if (currentCode && autoCode && currentCode !== autoCode) {
      sessionStorage.removeItem('pending_discount_code');
      return;
    }

    isValidatingPending = true;
    sessionStorage.setItem('pending_code_validated', '1');

    try {
      // 1. Versuche pending Code + Auto-Apply zu stacken
      const combinedCodes = autoCode
        ? [autoCode, pendingCode.toUpperCase()].join(',')
        : pendingCode;

      const stackResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: combinedCodes })
      });

      if (!stackResponse.ok) {
        sessionStorage.removeItem('pending_discount_code');
        await restoreAutoApplyCode();
        return;
      }

      const stackCart = await stackResponse.json();
      const pendingApplied = (stackCart.discount_codes || []).find(
        dc => dc.code?.toUpperCase() === pendingCode.toUpperCase() && dc.applicable !== false
      );

      if (pendingApplied) {
        const autoApplied = autoCode && (stackCart.discount_codes || []).find(
          dc => dc.code?.toUpperCase() === autoCode && dc.applicable !== false
        );
        sessionStorage.removeItem('pending_discount_code');
        if (autoApplied) {
          syncCartConfiguration();
        } else {
          window.CookieHandler?.setCookie?.(pendingCode, true);
          syncCartConfiguration();
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      // 2. Stacking fehlgeschlagen — pending Code ALLEIN testen
      const soloResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: pendingCode })
      });

      if (soloResponse.ok) {
        const soloCart = await soloResponse.json();
        const soloApplied = (soloCart.discount_codes || []).find(
          dc => dc.code?.toUpperCase() === pendingCode.toUpperCase() && dc.applicable !== false
        );

        if (soloApplied) {
          // Code allein gültig — Kunde kam explizit mit diesem Code, er hat Priorität
          sessionStorage.removeItem('pending_discount_code');
          window.CookieHandler?.setCookie?.(pendingCode, true);
          syncCartConfiguration();
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          return;
        }
      }

      // 3. Code weder gestackt noch allein gültig — verwerfen, Auto-Apply wiederherstellen
      sessionStorage.removeItem('pending_discount_code');
      await restoreAutoApplyCode();
    } catch (e) {
      sessionStorage.removeItem('pending_discount_code');
    } finally {
      isValidatingPending = false;
    }
  }

  async function restoreAutoApplyCode() {
    const autoCode = window.__autoApplyCode;
    if (!autoCode) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: autoCode })
      });
      window.CookieHandler?.setCookie?.(autoCode, true);
      syncCartConfiguration();
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {}
  }

  function updateCartConfigurationFromResponseImmediate(cartData, requestUrl) {
    try {
      const currentConfig = getCartConfiguration() || createCartConfiguration();
      currentConfig.cart = cartData;
      const activeDiscount = updateActiveDiscount();
      currentConfig.activeDiscount = activeDiscount;
      const activeCode = window.CookieHandler?.getActiveCode?.();
      currentConfig.activeCode = activeCode;
      setCartConfiguration(currentConfig);
      syncCartConfiguration();

      if (sessionStorage.getItem('pending_discount_code') && !sessionStorage.getItem('pending_code_validated')) {
        validatePendingCode(cartData);
      }
    } catch (error) {
    }
  }
  let configUpdateTimeout = null;
  function updateCartConfigurationFromResponse(cartData, requestUrl) {
    if (configUpdateTimeout) {
      clearTimeout(configUpdateTimeout);
    }
    configUpdateTimeout = setTimeout(() => {
      try {
        const currentConfig = getCartConfiguration() || createCartConfiguration();
        currentConfig.cart = cartData;
        const activeDiscount = updateActiveDiscount();
        currentConfig.activeDiscount = activeDiscount;
        const activeCode = window.CookieHandler?.getActiveCode?.();
        currentConfig.activeCode = activeCode;
        setCartConfiguration(currentConfig);
        setTimeout(() => {
          syncCartConfiguration();
        }, 200);
      } catch (error) {
      }
    }, 500);
  }
  function setupManualTriggers() {
  }
  return {
    getActiveCode,
    getActiveDiscount,
    bindCookieEvents,
    bindCartEvents,
    findDiscountTier,
    formatMoney,
    getMaxTier,
    fetchCartData,
    getCartConfiguration,
    setCartConfiguration,
    clearCartConfiguration,
    syncCartConfiguration,
    getCartData,
    getActiveDiscountData,
    getActiveCodeData,
    isCartConfigurationReady,
    onCartConfigUpdate,
    subscribeToCartEvents,
    unsubscribeFromCartEvents,
    dispatchCartEvent,
    queueCartUpdate,
    initialize,
    setupManualTriggers
  };
})();
let hasInitialized = false;
function safeInitialize() {
  if (hasInitialized) {
    return;
  }
  window.RosentalDiscount.initialize();
  hasInitialized = true;
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', safeInitialize);
} else {
  safeInitialize();
}
